/**
 * griglia.js — quello che la vista del giorno e la vista della settimana
 * dell'agenda hanno in comune: le ore della griglia, come si affiancano gli
 * appuntamenti sovrapposti, come si chiama un appuntamento e a che punto e'.
 *
 * Stava scritto due volte, una per vista (Agenda.jsx e AgendaSettimana.jsx),
 * e le due copie avevano gia' divergenze: la settimana posizionava con
 * l'ora del browser (getHours) invece che con quella della clinica, quindi
 * su una postazione col fuso sbagliato gli appuntamenti della settimana
 * stavano a un'altra ora di quelli del giorno. Stile «Console», 6/10/2026.
 *
 * Nessuna regola dell'agenda sta qui: chi puo' prenotare dove, le
 * sovrapposizioni che il server segnala, il legame alla visita, la sala
 * d'attesa li decidono le rotte. Qui si DISEGNA quello che le rotte dicono.
 */
import { useEffect, useState } from 'react';
import { oraDecimaleIta, oraIta } from '../../utils/oraItaliana';

export const ORA_INIZIO = 7;      // la griglia parte dalle 07:00
export const ORA_FINE = 22;       // l'ultima riga e' quella delle 22
/* Pixel per un'ora. Erano 60: un appuntamento di un quarto d'ora era alto
   15 px e ci stava a malapena l'ora, mezz'ora non bastava per ora e nome.
   A 80 un quarto d'ora porta ora e nome su una riga, mezz'ora su due, tre
   quarti anche la prestazione (il mockup «Agenda» ne mette 112: troppo
   poco giorno a video per la segreteria). Un numero solo: la posizione, il
   trascinamento e le linee di sfondo (variabile --ag-ora) lo leggono da qui. */
export const ALTEZZA_ORA = 80;
/* Un appuntamento di pochi minuti resta alto abbastanza da vederlo e da
   cliccarlo: il giorno e la settimana lo leggono da qui, non da due 18. */
export const ALTEZZA_MINIMA = 18;

/** Le ore della griglia: 7, 8, ... 22. */
export const ORE = (() => {
    const v = [];
    for (let h = ORA_INIZIO; h <= ORA_FINE; h += 1) v.push(h);
    return v;
})();

export const dueCifre = (n) => String(n).padStart(2, '0');

/** Dove sta un istante sulla griglia, in pixel dall'alto: l'ora della clinica. */
export const pixelDi = (iso) => (oraDecimaleIta(iso) - ORA_INIZIO) * ALTEZZA_ORA;

/**
 * L'etichetta di un'ora (colonna delle ore) che l'ora di adesso coprirebbe
 * (revisione del 6/10/2026: nei primi venti minuti di ogni ora «14:16» stava
 * sopra «14:00», e a meta' non se ne leggeva nessuno dei due). L'etichetta
 * dell'ora sta appena sotto la sua riga, quella di adesso a cavallo della
 * linea: si toccano da qualche minuto prima dell'ora a un terzo d'ora dopo,
 * e in quel tratto si legge solo l'ora di adesso. I due capi li prova lo
 * scenario 2605 con l'orologio della pagina fermo (alle 14:56, 15:05, 15:19 e
 * 15:40: nessuna etichetta coperta, e quella delle 15 tolta solo quando
 * serve). `linea` e' la posizione della linea in pixel, null senza linea.
 */
export const oraCoperta = (h, linea) => {
    if (linea == null) return false;
    const riga = (h - ORA_INIZIO) * ALTEZZA_ORA;
    return linea > riga - ALTEZZA_ORA / 16 && linea < riga + ALTEZZA_ORA / 3;
};

/** L'altezza della griglia: dalle 07:00 alla fine dell'ultima ora. */
export const ALTEZZA_GRIGLIA = (ORA_FINE - ORA_INIZIO + 1) * ALTEZZA_ORA;

/**
 * Dove sta un appuntamento sulla griglia: { top, height } in pixel, SEMPRE
 * dentro la griglia. Un appuntamento fuori dalle ore della griglia (le 04:30
 * di una visita notturna, i dati finti del beta) finiva a un `top` negativo,
 * disegnato SOPRA il corpo, sotto l'intestazione che gli stava sopra: si
 * vedeva a meta' e nessun clic lo prendeva. Adesso si ferma sul bordo della
 * griglia - in cima o in fondo - con l'altezza minima, e dice la sua ora nel
 * blocco: si vede e si apre (revisione della fase 3, 14/10/2026).
 */
export const riquadroDi = (x) => {
    const inizio = pixelDi(x.inizio);
    const fine = pixelDi(x.fine);
    const dentro = inizio >= 0 && fine <= ALTEZZA_GRIGLIA;
    /* Fuori dalle ore: alto quanto mezz'ora, che e' la misura piu' piccola
       con ora e nome leggibili, fermo sul bordo. */
    const minima = dentro ? ALTEZZA_MINIMA : ALTEZZA_ORA / 2;
    const da = Math.min(Math.max(inizio, 0), ALTEZZA_GRIGLIA - minima);
    const a = Math.min(Math.max(fine, da + minima), ALTEZZA_GRIGLIA);
    return { top: da, height: a - da };
};

/** «08:45:00» (un orario di una fascia di lavoro) in pixel dall'alto. */
export const pixelDiOrario = (t) => {
    if (!t) return 0;
    const [h, m] = String(t).split(':').map(Number);
    return (h + m / 60 - ORA_INIZIO) * ALTEZZA_ORA;
};

/** I minuti di un appuntamento. */
export const durataMinuti = (x) => Math.max(0, Math.round((new Date(x.fine) - new Date(x.inizio)) / 60000));

/** «09:30–10:15» in ora italiana. */
export const orarioDi = (x) => `${oraIta(x.inizio)}–${oraIta(x.fine)}`;

/** Chi o che cosa: il titolo di un blocco, il paziente in archivio, o il nome lasciato al telefono. */
export const nomeAppuntamento = (x) => {
    if (x.tipo === 'blocco') return x.titolo || '';
    if (x.paz_cognome) return `${x.paz_cognome} ${x.paz_nome || ''}`.trim();
    return x.paziente_nome || '';
};

/** Le prestazioni prenotate, in una riga. */
export const prestazioniDi = (x) => (x.prestazioni || []).map((p) => p.descrizione).filter(Boolean).join(', ');

/**
 * A che punto e' l'appuntamento, come lo dicono i campi della rotta. Lo
 * stesso ordine dei pallini della griglia: verde («uscito», services/
 * salaAttesa.js, USCITA_DEL_PAZIENTE) vince sul giallo («arrivato»); il
 * giallo dice ARRIVATO e non «in sala d'attesa», che ha una definizione
 * sola, sul server (il numero accanto ad Agenda).
 * «IN VISITA» (SVILUPPO, 6/10/2026, dal ramo dei tester): l'appuntamento
 * «in_corso» - il «Trasforma in visita» fatto dal medico - fra l'uscita e
 * l'arrivo: il verde vince anche su questo, e trasformato dalla segreteria
 * resta «arrivato», perche' il paziente aspetta ancora il medico. Lo stato
 * dell'appuntamento, non la presenza: la stessa condizione del riquadro
 * «In visita» del pannello (Agenda.jsx).
 */
export function statoDi(x) {
    if (x.stato === 'annullato') return 'annullato';
    if (x.tipo === 'blocco') return 'blocco';
    if (x.uscito) return 'uscito';
    if (x.tipo === 'visita' && x.stato === 'in_corso') return 'in_visita';
    if (x.presenza === 'presente') return 'arrivato';
    if (x.presenza === 'assente' || x.stato === 'non_presentato') return 'non_presentato';
    return 'prenotato';
}

/**
 * Dispone gli appuntamenti di una colonna su sotto-colonne affiancate:
 * chi si accavalla non deve coprire l'altro.
 *
 * PER GRUPPI, NON UNO PER UNO (6/10/2026). Un gruppo e' una catena di
 * appuntamenti che si accavallano fra loro; tutti quelli del gruppo hanno la
 * stessa larghezza, la colonna intera divisa per le sotto-colonne che il
 * gruppo usa. Prima ognuno prendeva la larghezza dal NUMERO di quelli che
 * toccava lui: in un gruppo di tre, quello che ne toccava uno solo era largo
 * meta' colonna e copriva quello in terza posizione, largo un terzo. Lo ha
 * trovato la prova dell'Agenda (scenario 2605) sulla copia, dove gli
 * appuntamenti delle prove si accumulano nella stessa fascia.
 *
 * Gli annullati NON entrano nella disposizione: stanno dietro ai vivi, a
 * tutta colonna (A4, Alex 13/10/2026). Due appuntamenti che si toccano solo
 * agli estremi non stanno nello stesso gruppo.
 *
 * «SOVRAPPOSTO» NON SI DECIDE QUI (revisione del 6/10/2026): arriva dalla
 * rotta, `x.sovrapposto` (agendaController, SI_ACCAVALLA: la stessa
 * definizione dell'avviso al salvataggio, stanza compresa). Qui c'era una
 * seconda copia, senza la stanza. Affiancare e' disegno; dire che due
 * appuntamenti si accavallano e' una regola, e sta sul server.
 */
export function disponi(appuntamenti) {
    /* L'INTERVALLO CHE SI DISEGNA, NON QUELLO DELL'ORARIO (fase 3, rc2). Un
       appuntamento fuori dalle ore della griglia si ferma sul suo bordo con
       un'altezza minima (riquadroDi): due appuntamenti della notte, uno alle
       04:30 e uno alle 06:30, non si accavallano nell'orario ma stanno nello
       stesso punto del disegno, e il secondo copriva il primo (misurato: il
       primo non si prendeva con un clic). Si dispone sui pixel: chi sta dentro
       la griglia ha i suoi, quelli veri; chi sta fuori ha il rettangolo che
       ricevera'. */
    const intervallo = (a) => {
        const i = pixelDi(a.inizio);
        const f = pixelDi(a.fine);
        if (i >= 0 && f <= ALTEZZA_GRIGLIA) return [i, f];
        const r = riquadroDi(a);
        return [r.top, r.top + r.height];
    };
    /* GLI ANNULLATI DIETRO (Alex, 13/10/2026, A4): un appuntamento annullato
       non prende una sotto-colonna e non dimezza chi c'e' - bastava un
       «annullato e riprenotato allo stesso orario» per stringere il blocco
       vivo -. Resta in griglia, barrato, a tutta colonna e SOTTO i vivi
       (`dietro`, z-index nel foglio): chi telefona per spostare lo trova, e
       dove un vivo lo copre si legge il vivo. */
    const annullati = appuntamenti.filter((a) => a.stato === 'annullato')
        .map((a) => ({ ...a, colonna: 0, insieme: 1, dietro: true }));
    const ordinati = appuntamenti.filter((a) => a.stato !== 'annullato')
        .map((a) => ({ ...a, _iv: intervallo(a) }))
        .sort((a, b) => a._iv[0] - b._iv[0] || a._iv[1] - b._iv[1]);
    const disposti = [];
    let gruppo = [];
    let colonne = [];          // fine dell'ultimo appuntamento per sotto-colonna, nel gruppo
    let fineGruppo = -Infinity;
    const chiudiGruppo = () => {
        gruppo.forEach((x) => { x.insieme = colonne.length; });
        disposti.push(...gruppo);
        gruppo = [];
        colonne = [];
        fineGruppo = -Infinity;
    };

    for (const { _iv, ...a } of ordinati) {
        const [inizio, fine] = _iv;
        if (gruppo.length && inizio >= fineGruppo) chiudiGruppo();
        let col = colonne.findIndex((f) => f <= inizio);
        if (col === -1) { col = colonne.length; colonne.push(fine); } else colonne[col] = fine;
        fineGruppo = Math.max(fineGruppo, fine);
        gruppo.push({ ...a, colonna: col });
    }
    chiudiGruppo();

    return [...annullati, ...disposti];
}

/**
 * L'ora che passa, per la linea dell'ora: si ridisegna ogni mezzo minuto.
 * Prima la linea si calcolava solo quando cambiavano giorno o dati, e
 * un'agenda lasciata aperta la mattina la teneva ferma all'ora d'apertura.
 * Nessuna richiesta al server: e' solo l'orologio.
 */
export function useAdesso() {
    const [adesso, setAdesso] = useState(() => new Date());
    useEffect(() => {
        const t = setInterval(() => setAdesso(new Date()), 30000);
        return () => clearInterval(t);
    }, []);
    return adesso;
}
